import { mapStorageFailure, StorageError } from '../errors.js'
import type { BlobStore } from '../stores.js'

/**
 * OPFS blob adapter — PRD §8.5, §11.2.
 * Blobs are immutable: `put` is create-only, and OPFS `createWritable/close`
 * gives commit-on-close semantics, so a crash mid-write cannot replace a
 * previously committed revision (LOC-02).
 */

export interface OpfsWritable {
  write(data: Uint8Array): Promise<void>
  close(): Promise<void>
  abort?(): Promise<void>
}

export interface OpfsFile {
  arrayBuffer(): Promise<ArrayBuffer>
}

export interface OpfsFileHandle {
  createWritable(options?: { keepExistingData?: boolean }): Promise<OpfsWritable>
  getFile(): Promise<OpfsFile>
}

export interface OpfsDirectoryHandle {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<OpfsDirectoryHandle>
  getFileHandle(name: string, options?: { create?: boolean }): Promise<OpfsFileHandle>
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>
  keys?(): AsyncIterableIterator<string>
}

interface OpfsStorageManager {
  getDirectory(): Promise<OpfsDirectoryHandle>
}

export function getNavigatorOpfsRoot(): OpfsDirectoryHandle {
  const storage = (globalThis as { navigator?: { storage?: OpfsStorageManager } }).navigator
    ?.storage
  if (storage === undefined || typeof storage.getDirectory !== 'function') {
    throw new StorageError('OP_STORAGE_WRITE_FAILED', 'OPFS is unavailable in this environment')
  }
  return {
    getDirectoryHandle: (name, options) =>
      storage.getDirectory().then((root) => root.getDirectoryHandle(name, options)),
    getFileHandle: (name, options) =>
      storage.getDirectory().then((root) => root.getFileHandle(name, options)),
    removeEntry: (name, options) =>
      storage.getDirectory().then((root) => root.removeEntry(name, options)),
  }
}

function isNotFound(error: unknown): boolean {
  return (error as { name?: unknown }).name === 'NotFoundError'
}

function fileNameFor(key: string): string {
  return encodeURIComponent(key)
}

function keyFromFileName(name: string): string {
  return decodeURIComponent(name)
}

export class OpfsBlobStore implements BlobStore {
  constructor(
    private readonly root: OpfsDirectoryHandle,
    private readonly directoryName = 'blobs',
  ) {}

  private directory(): Promise<OpfsDirectoryHandle> {
    return this.root.getDirectoryHandle(this.directoryName, { create: true })
  }

  async put(key: string, data: Uint8Array): Promise<void> {
    if (await this.has(key)) {
      throw new StorageError(
        'OP_STORAGE_REVISION_CONFLICT',
        `blob ${key} already exists; blobs are immutable`,
      )
    }
    let writable: OpfsWritable
    try {
      const directory = await this.directory()
      const handle = await directory.getFileHandle(fileNameFor(key), { create: true })
      writable = await handle.createWritable()
    } catch (error) {
      throw mapStorageFailure(error, `could not create blob ${key}`)
    }
    try {
      await writable.write(data)
      await writable.close()
    } catch (error) {
      if (writable.abort !== undefined) await writable.abort()
      throw mapStorageFailure(error, `could not write blob ${key}`)
    }
  }

  async get(key: string): Promise<Uint8Array | null> {
    try {
      const directory = await this.directory()
      const handle = await directory.getFileHandle(fileNameFor(key))
      const file = await handle.getFile()
      return new Uint8Array(await file.arrayBuffer())
    } catch (error) {
      if (isNotFound(error)) return null
      throw mapStorageFailure(error, `could not read blob ${key}`)
    }
  }

  async has(key: string): Promise<boolean> {
    try {
      const directory = await this.directory()
      await directory.getFileHandle(fileNameFor(key))
      return true
    } catch (error) {
      if (isNotFound(error)) return false
      throw mapStorageFailure(error, `could not stat blob ${key}`)
    }
  }

  async delete(key: string): Promise<void> {
    try {
      const directory = await this.directory()
      await directory.removeEntry(fileNameFor(key))
    } catch (error) {
      if (!isNotFound(error)) throw mapStorageFailure(error, `could not delete blob ${key}`)
    }
  }

  async list(): Promise<string[]> {
    const directory = await this.directory()
    const keys: string[] = []
    if (directory.keys !== undefined) {
      for await (const name of directory.keys()) keys.push(keyFromFileName(name))
    }
    return keys.sort()
  }
}
